import { useCallback, useState } from 'react'

export interface Disclosure<T extends NonNullable<unknown>> {
  value: T | null
  isOpen: boolean
  open: (value: T) => void
  close: () => void
  onOpenChange: (isOpen: boolean) => void
}

/**
 * Open/closed state for a dialog or sheet. `value` carries the payload the overlay needs
 * while open (an id or entity) and is `null` when closed, so it can be passed straight to
 * overlays that take `entity | null`. `onOpenChange` only ever closes: opening needs a
 * payload, so go through `open`.
 */
export function useDisclosure<T extends NonNullable<unknown> = true>(): Disclosure<T> {
  const [value, setValue] = useState<T | null>(null)

  const open = useCallback((nextValue: T) => setValue(nextValue), [])
  const close = useCallback(() => setValue(null), [])
  const onOpenChange = useCallback((isOpen: boolean) => {
    if (!isOpen) setValue(null)
  }, [])

  return { value, isOpen: value !== null, open, close, onOpenChange }
}
